/* elevate.js
   Premium behaviors for Elevate OS2 theme:
   - Sticky header scroll state
   - Mobile drawer menu (open/close, ESC, click outside, focus trap)
   - Parallax hero (optional via settings: use_parallax)
   - Product image swapping (PDP thumbnails)
   - Tabs (PDP)
   - Vehicle picker (Make/Model/Trim/Year) powered by assets/fitment-data.json
*/

(() => {
  const qs = (s, el = document) => el.querySelector(s);
  const qsa = (s, el = document) => Array.from(el.querySelectorAll(s));

  // ---------- Sticky header scroll state ----------
  const header = qs('.hdr') || qs('[data-header]');
  const onScroll = () => {
    if (!header) return;
    header.classList.toggle('is-scrolled', window.scrollY > 8);
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  // ---------- Mobile drawer menu ----------
  const drawer = qs('[data-drawer]');
  const openBtn = qs('[data-drawer-open]');
  const closeBtns = qsa('[data-drawer-close]');

  let lastFocus = null;

  const trapFocus = (container, e) => {
    if (e.key !== 'Tab') return;
    const focusables = qsa('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])', container)
      .filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const openDrawer = () => {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.hidden = false;
    document.body.classList.add('no-scroll');
    // Focus first focusable
    const panel = qs('.drawer__panel', drawer) || drawer;
    const first = qs('button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])', panel);
    first && first.focus();
    if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
  };

  const closeDrawer = () => {
    if (!drawer) return;
    drawer.hidden = true;
    document.body.classList.remove('no-scroll');
    if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  };

  if (openBtn && drawer) {
    openBtn.addEventListener('click', () => {
      if (drawer.hidden) openDrawer();
      else closeDrawer();
    });
  }
  closeBtns.forEach(btn => btn.addEventListener('click', closeDrawer));

  if (drawer) {
    drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeDrawer();
      trapFocus(drawer, e);
    });

    // Close when clicking outside the panel
    drawer.addEventListener('click', (e) => {
      const backdrop = e.target.closest('.drawer__backdrop');
      if (backdrop) closeDrawer();

      // Close after tapping any drawer link
      const link = e.target.closest('a');
      if (link && link.classList.contains('drawer__link')) closeDrawer();
    });
  }

  // ---------- Parallax hero ----------
  // Supported markup: .hero2__bg using CSS variable --hero-offset
  const heroBg = qs('.hero2__bg');
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // If you want to hard-disable via dataset, set <body data-parallax="false">
  const parallaxAllowed = !prefersReduced && document.body.getAttribute('data-parallax') !== 'false';

  if (heroBg && parallaxAllowed) {
    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
    const tick = () => {
      const y = window.scrollY || 0;
      // subtle movement
      const offset = clamp(y * -0.12, -28, 0);
      heroBg.style.setProperty('--hero-offset', `${offset}px`);
    };
    tick();
    window.addEventListener('scroll', tick, { passive: true });
  }

  // ---------- Product image swap ----------
  qsa('[data-swap-image]').forEach(btn => {
    btn.addEventListener('click', () => {
      const src = btn.getAttribute('data-swap-image');
      const img = qs('.product__img');
      if (img && src) img.src = src;
    });
  });

  // ---------- Tabs ----------
  qsa('[data-tabs]').forEach(tabs => {
    const wrap = tabs.closest('.product__info') || document;
    const buttons = qsa('[data-tab]', tabs);
    const panels = qsa('[data-panel]', wrap);

    buttons.forEach(b => b.addEventListener('click', () => {
      const id = b.getAttribute('data-tab');
      buttons.forEach(x => x.classList.toggle('is-active', x === b));
      panels.forEach(p => p.classList.toggle('is-active', p.getAttribute('data-panel') === id));
    }));
  });

  // ---------- Vehicle picker (Make/Model/Trim/Year) ----------
  // fitment-data.json structure:
  // { "makes": { "Ford": { "F-250/350": { "Platinum": [2017,2018] }}}}
  async function initVehiclePicker(root) {
    const makeSel = qs('[data-make]', root);
    const modelSel = qs('[data-model]', root);
    const trimSel = qs('[data-trim]', root);
    const yearSel = qs('[data-year]', root);
    const goBtn = qs('[data-go]', root);
    const clearBtn = qs('[data-clear]', root);

    if (!makeSel || !modelSel || !trimSel || !yearSel || !goBtn || !clearBtn) return;

    let data = null;
    try {
      const res = await fetch((window.Shopify?.routes?.root || '/') + 'assets/fitment-data.json', { cache: 'no-store' });
      data = await res.json();
    } catch (e) {
      data = { makes: {} };
    }

    const resetSelect = (sel) => {
      sel.innerHTML = '<option value="">Select</option>';
      sel.disabled = true;
    };
    const enable = (sel) => (sel.disabled = false);

    resetSelect(modelSel);
    resetSelect(trimSel);
    resetSelect(yearSel);
    goBtn.disabled = true;

    // Populate makes
    Object.keys(data.makes || {}).sort().forEach(m => {
      const opt = document.createElement('option');
      opt.value = m;
      opt.textContent = m;
      makeSel.appendChild(opt);
    });

    makeSel.addEventListener('change', () => {
      resetSelect(modelSel);
      resetSelect(trimSel);
      resetSelect(yearSel);
      goBtn.disabled = true;

      const make = makeSel.value;
      if (!make) return;

      enable(modelSel);
      Object.keys(data.makes?.[make] || {}).sort().forEach(model => {
        const opt = document.createElement('option');
        opt.value = model;
        opt.textContent = model;
        modelSel.appendChild(opt);
      });
    });

    modelSel.addEventListener('change', () => {
      resetSelect(trimSel);
      resetSelect(yearSel);
      goBtn.disabled = true;

      const make = makeSel.value;
      const model = modelSel.value;
      if (!make || !model) return;

      enable(trimSel);
      Object.keys(data.makes?.[make]?.[model] || {}).sort().forEach(trim => {
        const opt = document.createElement('option');
        opt.value = trim;
        opt.textContent = trim;
        trimSel.appendChild(opt);
      });
    });

    trimSel.addEventListener('change', () => {
      resetSelect(yearSel);
      goBtn.disabled = true;

      const make = makeSel.value;
      const model = modelSel.value;
      const trim = trimSel.value;
      if (!make || !model || !trim) return;

      enable(yearSel);
      const years = (data.makes?.[make]?.[model]?.[trim] || []).slice().sort((a, b) => a - b);
      years.forEach(y => {
        const opt = document.createElement('option');
        opt.value = String(y);
        opt.textContent = String(y);
        yearSel.appendChild(opt);
      });
    });

    yearSel.addEventListener('change', () => {
      const make = makeSel.value;
      const model = modelSel.value;
      const trim = trimSel.value;
      const year = yearSel.value;
      goBtn.disabled = !(make && model && trim && year);
    });

    goBtn.addEventListener('click', () => {
      const make = makeSel.value;
      const model = modelSel.value;
      const trim = trimSel.value;
      const year = yearSel.value;
      if (!(make && model && trim && year)) return;

      // Default route to all products filtered by metafields (requires Search & Discovery filters configured)
      const params = new URLSearchParams();
      params.set('filter.p.m.custom.fitment_make', make);
      params.set('filter.p.m.custom.fitment_model', model);
      params.set('filter.p.m.custom.fitment_trim', trim);
      params.set('filter.p.m.custom.fitment_years', year);

      // If you want to restrict to a Parts collection, change to /collections/parts
      window.location.href = '/collections/all?' + params.toString();
    });

    clearBtn.addEventListener('click', () => {
      makeSel.value = '';
      resetSelect(modelSel);
      resetSelect(trimSel);
      resetSelect(yearSel);
      goBtn.disabled = true;
    });
  }

  qsa('[data-vehicle-picker]').forEach(initVehiclePicker);
})();
